import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Avatar, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import clsx from 'clsx';

import { UserContext } from '@/app/UserProvider';
import LogoutIcon from '@/assets/logout.svg?react';
import { useResponsive } from '@/hooks/useResponsive';
import { useDownloadInfo } from '@/pages/Download/hooks/useDownloadInfo';
import { removeAuthToken } from '@/shared/lib/client';
import { useBannerStore } from '@/store/useBannerStore';
import { generateInternalSSOUrl, isInternalDomain } from '@/utils/const';
import { openDownloadInNewWindow, safeDownload } from '@/utils/downloadHelper';
import {
  getRecommendedDownloadType,
  getSystemInfo,
  isSystemSupported,
} from '@/utils/systemDetector';

import previewLogo from './PREVIEW-logo.png';
import css from './index.module.less';
import previewLogoCN from './preview-logo-cn.png';

export function NavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { downloadInfo } = useDownloadInfo();
  const { user, setUser } = useContext(UserContext);
  const [recommendedType, setRecommendedType] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentLogo, setCurrentLogo] = useState(previewLogo);
  const { isBannerVisible } = useBannerStore();

  // 响应式状态管理
  const { isMobile } = useResponsive();

  // 动态高度测量（仅移动端）
  const [bannerHeight, setBannerHeight] = useState(0);
  const navFixedRef = useRef<HTMLDivElement | null>(null);
  const measureHeights = useCallback(() => {
    const bannerEl = document.getElementById('global-banner');
    const newBannerHeight = bannerEl ? bannerEl.offsetHeight : 0;
    setBannerHeight(newBannerHeight || 0);
  }, []);

  // 提前到布局阶段进行首次测量，避免首屏抖动（仅在移动端执行动态测量）
  useLayoutEffect(() => {
    if (!isBannerVisible) {
      setBannerHeight(0);
      return;
    }

    if (!isMobile) {
      // 桌面端：Banner 高度固定 32px，无需动态测量
      setBannerHeight(32);
      return;
    }

    // 移动端：进行实时测量
    measureHeights();
    const bannerEl = document.getElementById('global-banner');

    let ro: ResizeObserver | null = null;
    if (bannerEl && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => measureHeights());
      ro.observe(bannerEl);
    }

    const handleResize = () => measureHeights();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (ro && bannerEl) ro.unobserve(bannerEl);
    };
  }, [isMobile, isBannerVisible, measureHeights]);

  // 根据当前语言选择合适的 logo
  const getCurrentLogo = useCallback(() => {
    const currentLanguage = i18n.language;
    const isChinese = ['zh', 'zh-CN', 'zh-Hans', 'zh-Hant', 'zh-TW', 'zh-HK'].some((lang) =>
      currentLanguage.toLowerCase().startsWith(lang.toLowerCase()),
    );
    return isChinese ? previewLogoCN : previewLogo;
  }, [i18n.language]);

  // 监听语言变化，更新 logo
  useEffect(() => {
    const updateLogo = () => {
      setCurrentLogo(getCurrentLogo());
    };

    // 初始化时设置 logo
    updateLogo();

    // 监听语言变化
    i18n.on('languageChanged', updateLogo);

    return () => {
      i18n.off('languageChanged', updateLogo);
    };
  }, [getCurrentLogo, i18n]);

  // 滚动监听 - 首页时根据滚动位置显示背景色，其他页面始终显示背景色
  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname === '/') {
        const scrollTop = document.documentElement.scrollTop;
        const heroTitleElement = document.getElementById('hero-title');

        // 动态获取 navbar 可见高度
        const navFixedEl = navFixedRef.current;
        const NAVBAR_HEIGHT = navFixedEl ? navFixedEl.offsetHeight : 86;
        const BANNER_HEIGHT = isBannerVisible ? (isMobile ? bannerHeight : 32) : 0;

        if (heroTitleElement) {
          const heroTitleRect = heroTitleElement.getBoundingClientRect();
          const heroTitleTop = heroTitleRect.top + scrollTop;
          // 当滚动到 hero-title 上方 12px（考虑 banner 与 navbar 高度）后，显示背景
          const threshold = heroTitleTop - 12 - NAVBAR_HEIGHT - BANNER_HEIGHT;
          setIsScrolled(scrollTop > threshold);
        } else {
          // 找不到 hero-title 时：兜底为超出一屏高度后显示
          const windowHeight = window.innerHeight;
          setIsScrolled(scrollTop > windowHeight);
        }
      } else {
        // 其他页面：始终显示背景色
        setIsScrolled(true);
      }
    };

    // 添加滚动事件监听
    window.addEventListener('scroll', handleScroll, { passive: true });

    // 初始检查
    handleScroll();

    // 清理函数
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname, isBannerVisible, bannerHeight, isMobile]);

  // 初始化时检测用户系统
  useEffect(() => {
    const systemInfo = getSystemInfo();
    const recommendedDownloadType = getRecommendedDownloadType();
    setRecommendedType(recommendedDownloadType);

    // 在开发环境下输出调试信息
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('NavBar 检测到的系统信息:', systemInfo);
      // eslint-disable-next-line no-console
      console.log('NavBar 推荐下载类型:', recommendedDownloadType);
    }
  }, []);

  function handleHome() {
    navigate('/');
  }

  // 智能下载处理函数

  // 智能下载处理函数
  const handleDownload = useCallback(async () => {
    if (!downloadInfo || !recommendedType) {
      // 如果没有下载信息或无法检测系统，跳转到下载页面
      navigate('/download');
      return;
    }

    let targetDownloadInfo = null;

    // 根据推荐类型获取下载信息
    switch (recommendedType) {
      case 'apple':
        targetDownloadInfo = downloadInfo.arm;
        break;
      case 'intel':
        targetDownloadInfo = downloadInfo.intel;
        break;
      case 'windows':
        targetDownloadInfo = downloadInfo.windows;
        break;
      default:
        // 不支持的系统，跳转到下载页面
        navigate('/download');
        return;
    }

    if (targetDownloadInfo) {
      try {
        // 尝试安全下载
        const downloadSuccess = await safeDownload(targetDownloadInfo);

        if (!downloadSuccess) {
          // 如果下载失败，尝试在新窗口打开
          const downloadUrl = targetDownloadInfo.dmgUrl || targetDownloadInfo.url;
          if (downloadUrl) {
            openDownloadInNewWindow(downloadUrl);
          } else {
            // 最后的备用方案：跳转到下载页面
            navigate('/download');
          }
        }
      } catch (error) {
        console.error('NavBar 下载失败:', error);
        // 出错时跳转到下载页面
        navigate('/download');
      }
    } else {
      // 没有下载信息，跳转到下载页面
      navigate('/download');
    }
  }, [downloadInfo, recommendedType, navigate]);

  // 处理不支持系统的点击事件
  const handleUnsupportedClick = useCallback(() => {
    navigate('/download');
  }, [navigate]);

  // 检查是否支持当前系统
  const systemSupported = isSystemSupported();

  // 处理登录
  const handleLogin = useCallback(() => {
    if (isInternalDomain()) {
      // 内部域名：直接跳转到 SSO 登录页
      window.location.href = generateInternalSSOUrl();
    } else {
      // 外部域名：跳转到登录页面
      sessionStorage.setItem('pendingRedirect', '/dashboard');
      navigate('/login?redirect=/dashboard');
    }
  }, [navigate]);

  // 处理退出登录
  const handleLogout = useCallback(async () => {
    try {
      // Remove local cookie
      removeAuthToken();

      // Clear user state
      setUser(null);

      // Clear the login success flag so next login will show toast again
      sessionStorage.removeItem('hasShownLoginSuccess');
      // Clear the pending redirect flag
      sessionStorage.removeItem('pendingRedirect');

      toast.success(t('loginSuccess.logoutSuccessful'));

      // Refresh page to recheck login status
      window.location.reload();
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if backend call fails, remove local cookie
      removeAuthToken();
      setUser(null);

      // Clear the login success flag so next login will show toast again
      sessionStorage.removeItem('hasShownLoginSuccess');
      // Clear the pending redirect flag
      sessionStorage.removeItem('pendingRedirect');

      toast.error(t('loginSuccess.logoutFailed'));

      // Refresh page to recheck login status
      window.location.reload();
    }
  }, [t, setUser]);

  // 处理个人设置
  const handleSettings = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  // 判断是否为内部域名
  const isInternal = isInternalDomain();

  // 用户下拉菜单配置
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div className="user-info">
          <div className="user-name">{user?.displayName || 'User'}</div>
          <div className="user-email">{user?.email || user?.mail || ''}</div>
        </div>
      ),
      disabled: true,
      className: 'user-info-item',
    },
    {
      type: 'divider',
      className: 'user-info-divider',
    },
    {
      key: 'settings',
      label: '个人设置',
      onClick: handleSettings,
      className: 'menu-item settings-item',
    },
    // 仅在外部域名时显示退出登录按钮
    ...(!isInternal
      ? [
          {
            key: 'logout',
            label: (
              <div className="logout-item">
                <span>{t('loginSuccess.logoutButton')}</span>
                <LogoutIcon />
              </div>
            ),
            onClick: handleLogout,
            className: 'menu-item',
          },
        ]
      : []),
  ];

  return (
    <>
      <div
        className={clsx(css['nav-bar'], isBannerVisible && css['nav-bar--with-banner'])}
        style={{ top: isMobile && isBannerVisible ? bannerHeight : undefined }}
      >
        <div
          ref={navFixedRef}
          className={clsx(css['nav-bar__fixed'], isScrolled && css['nav-bar__fixed--scrolled'])}
        >
          <div className={clsx(css['nav-bar-container'])}>
            <div className={clsx(css['logo'])} onClick={handleHome}>
              <img className={clsx(css['logo-icon'])} src={currentLogo} alt="logo" />
            </div>

            {/* 桌面端导航 */}
            {!isMobile && (
              <div className={clsx(css['nav-actions'])}>
                {/* 用户头像或登录按钮 */}
                {user ? (
                  <Dropdown
                    menu={{ items: userMenuItems }}
                    trigger={['click']}
                    placement="bottomRight"
                    overlayClassName={'user-dropdown'}
                  >
                    <div className={clsx(css['user-avatar-wrapper'])}>
                      <Avatar
                        src={user.avatar}
                        size={32}
                        shape="square"
                        style={{
                          borderRadius: '2px',
                          border: '0.6px solid #71C9C0',
                          background: '#71C9C038',
                          fontSize: '16px',
                          lineHeight: '16px',
                          fontWeight: 600,
                          color: '#32EDDA',
                        }}
                      >
                        {user.displayName?.charAt(0).toUpperCase() || 'U'}
                      </Avatar>
                    </div>
                  </Dropdown>
                ) : (
                  <div
                    className={clsx(css['login-btn'])}
                    onClick={handleLogin}
                    style={{
                      width: '88px',
                      height: '32px',
                      borderRadius: '2px',
                      border: '0.6px solid #71C9C0',
                      background: '#FFFFFF0A',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: '14px',
                      lineHeight: '16px',
                      fontWeight: 500,
                      color: '#DAF4F4',
                      cursor: 'pointer',
                    }}
                  >
                    {t('navbar.login')}
                  </div>
                )}

                {/* 下载 IDE 按钮 */}
                <div
                  className={clsx(
                    css['download-btn'],
                    !systemSupported && css['download-btn--disabled'],
                  )}
                  onClick={systemSupported ? handleDownload : handleUnsupportedClick}
                  title={
                    systemSupported
                      ? t('navbar.downloadTooltipSupported')
                      : t('navbar.downloadTooltipUnsupported')
                  }
                >
                  <span className={clsx(css['download-text'])}>{t('navbar.download')}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
